const DEFAULT_EXPENSE_CATEGORY = "TRANSPORT";
const ISO_DATE_PREFIX_PATTERN = /^(\d{4}-\d{2}-\d{2})/;

const toDateInputString = (value) => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? "" : value.toISOString().slice(0, 10);
  }

  if (value === null || value === undefined) {
    return "";
  }

  const rawValue = String(value).trim();
  if (!rawValue) {
    return "";
  }

  const isoDateMatch = rawValue.match(ISO_DATE_PREFIX_PATTERN);
  if (isoDateMatch) {
    return isoDateMatch[1];
  }

  const parsedDate = new Date(rawValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  return parsedDate.toISOString().slice(0, 10);
};

const normalizeExpenseCategory = (value) => {
  const normalizedValue = String(value || "").trim().toUpperCase();
  return normalizedValue || DEFAULT_EXPENSE_CATEGORY;
};

const normalizeExpenseComment = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value);
};

export const buildDefaultExpenseForm = (referenceDate = new Date()) => ({
  periodType: "ONE_TIME",
  start_date: toDateInputString(referenceDate),
  end_date: "",
  category: DEFAULT_EXPENSE_CATEGORY,
  amount: "",
  comment: "",
});

export const resolveExpensePeriodType = (expense) => {
  const normalizedMode = String(expense?.mode || "").trim().toUpperCase();
  const hasRawStartDate = expense?.start_date !== null && expense?.start_date !== undefined && expense?.start_date !== "";
  const hasRawEndDate = expense?.end_date !== null && expense?.end_date !== undefined && expense?.end_date !== "";
  const hasLegacyDate = expense?.date !== null && expense?.date !== undefined && expense?.date !== "";
  const normalizedStartDate = toDateInputString(expense?.start_date ?? expense?.date);
  const normalizedEndDate = toDateInputString(expense?.end_date);

  if (normalizedMode === "ONE_TIME") {
    return "ONE_TIME";
  }

  if (normalizedMode === "MONTHLY") {
    return normalizedEndDate ? "RANGE" : "ONGOING";
  }

  if (hasLegacyDate && !hasRawStartDate) {
    return "ONE_TIME";
  }

  if (hasRawEndDate) {
    return normalizedStartDate && normalizedStartDate === normalizedEndDate
      ? "ONE_TIME"
      : "RANGE";
  }

  return hasRawStartDate ? "ONGOING" : "ONE_TIME";
};

export const normalizeExpenseForForm = (expense, referenceDate = new Date()) => {
  const periodType = resolveExpensePeriodType(expense);
  const fallbackStartDate = toDateInputString(referenceDate);
  const startDate = toDateInputString(expense?.start_date ?? expense?.date) || fallbackStartDate;
  const endDate = toDateInputString(expense?.end_date);

  return {
    periodType,
    start_date: startDate,
    end_date: periodType === "RANGE" ? endDate : "",
    category: normalizeExpenseCategory(expense?.category),
    amount:
      expense?.amount === null || expense?.amount === undefined
        ? ""
        : String(expense.amount),
    comment: normalizeExpenseComment(expense?.comment),
  };
};

export const buildExpensePayload = (
  form,
  {
    missionId,
    includeMissionId = true,
  } = {},
) => {
  const normalizedPeriodType = String(form?.periodType || "ONE_TIME").trim().toUpperCase();
  const normalizedStartDate = toDateInputString(form?.start_date);
  const normalizedEndDate = toDateInputString(form?.end_date);
  const isOneTime = normalizedPeriodType === "ONE_TIME";
  const isOngoing = normalizedPeriodType === "ONGOING";

  const payload = {
    mode: isOneTime ? "ONE_TIME" : "MONTHLY",
    start_date: normalizedStartDate,
    end_date: isOneTime ? normalizedStartDate : isOngoing ? null : normalizedEndDate || null,
    category: normalizeExpenseCategory(form?.category),
    amount: Number(form?.amount || 0),
    comment: normalizeExpenseComment(form?.comment).trim() || null,
  };

  if (includeMissionId) {
    payload.mission_id = missionId;
  }

  return payload;
};

export const normalizeExpenseDateForInput = toDateInputString;
